import { ThunkAction, ThunkDispatch } from '../types';
import queryString from 'query-string';
import { State } from '../reducers';
import submitFormHack from '../utilities/submit-form';
import { captureErrorMessage } from '../utilities/analytics/error-reporting';

export interface ManageSessionRequest {
  sessionId: string;
  command: 'switch' | 'dissociate';
}

export const manageSession =
  (
    request: ManageSessionRequest,
    navigate: (url: string) => void,
    additionalParameters: { [key: string]: string } = {}
  ): ThunkAction =>
  (dispatch: ThunkDispatch, getState: () => State) => {
    const { csrfToken } = getState();

    dispatch({
      type: 'SESSION_REQUEST',
    });

    return fetch(`/rest/session`, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', 'X-CSRF-TOKEN': csrfToken },
      body: JSON.stringify(request),
    }).then(response =>
      response.json().then(data => {
        if (response.ok) {
          const redirectUrl: {
            url: string;
            query: {
              [key: string]: string;
            };
          } = queryString.parseUrl(data.redirectUrl);
          const currentParameters: { [key: string]: string } = queryString.parse(
            window.location.search
          );
          const mergedQuery = {
            ...currentParameters,
            ...additionalParameters,
            ...redirectUrl.query,
          };
          return navigate(`${redirectUrl.url}?${queryString.stringify(mergedQuery)}`);
        } else {
          captureErrorMessage('manageSession failed', {
            statusCode: response.status,
            formErrors: data.formErrors,
          });
        }
      })
    );
  };

export const continueWithCurrentSession =
  (navigate: (url: string) => void): ThunkAction =>
  async () => {
    navigate(`/login${getSanitizedQueryString()}`);
  };

export const doLogout =
  (continueUrl?: string): ThunkAction =>
  async (dispatch, getState) => {
    const state = getState();
    // [KIRBY-7902] TODO: replace with `submitFormHackReplacement` once the backend endpoint is ready.
    submitFormHack({
      method: 'POST',
      action: `/logout${getSanitizedQueryString(continueUrl)}`,
      payload: {
        csrfToken: state.hashedCsrfToken,
      },
    });
  };

const getSanitizedQueryString = (continueUrl?: string): string => {
  const params: Record<string, string> = queryString.parse(window.location.search);
  delete params.login_hint;
  delete params.prompt;
  if (continueUrl) {
    params.continue = continueUrl;
  }

  const paramsString: string = queryString.stringify(params);
  return paramsString && `?${paramsString}`;
};
